<template>
  <fieldset :class="_fs_class" :aria-labelledby="hint ? id + '_hint' : null">
    <cat-label
      :fieldset="true"
      :label="label"
      :label-id="id + '_lbl'"
      :placeholder="placeholder"
      :optional="!mandatory"
      :display-required="legendMandatory || !mandatory"
      :errors="_errors"
      :readonly="readonly"
      :inline="help && !!help.url"
    ></cat-label>
    <cat-help-trigger
      v-if="help && help.url"
      :source-id="id"
      :url="help.url"
      :title="label"
    />
    <div v-if="help && help.url" class="clearfix"></div>
    <cat-hint :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <slot></slot>
    <div :class="_ih_class">
      <template v-for="field in validateFieldOrder" :key="field">
        <template v-if="field == 'd'">
          <cat-input
            v-if="!attributeFields || (attributeFields && dayId)"
            :id="(attributeFields && dayId ? dayId : id) + '-day'"
            class="cat-date-field-item cat-date-field-day"
            :label="dayLabel ? dayLabel : resolveTxt('skin.day', 'Day')"
            :data-label="dayLabel ? dayLabel : resolveTxt('skin.day', 'Day')"
            :label-handled="hideFieldLabel"
            data-type="date-day"
            :offscreen-label="
              hideFieldLabel
                ? dayLabel
                  ? dayLabel
                  : resolveTxt('skin.day', 'Day')
                : null
            "
            :placeholder="
              hideFieldLabel
                ? dayLabel
                  ? dayLabel
                  : resolveTxt('skin.day', 'Day')
                : null
            "
            :data-nodetype="attributeFields ? 'attribute' : null"
            :value="dayValue"
            :readonly="readonly"
            :mandatory="attributeFields ? attrReq.day : mandatory"
            mask="0123456789"
            regex="^(3(0|1)|(2|1)\d|0?\d)?$"
            :max-length="2"
            :errors="_attrErrors.day.length > 0"
            @ui-change="dayChanged"
            @ui-focus="_onFocus"
            @ui-blur="_onBlur"
            @ui-input="_onInput($event, 'day')"
          />
        </template>
        <template v-else-if="field == 'm'">
          <div
            v-if="!attributeFields || (attributeFields && monthId)"
            class="cat-date-field-item select-holder cat-date-field-month"
            :data-label="
              monthLabel ? monthLabel : resolveTxt('skin.month', 'Month')
            "
            :data-nodetype="attributeFields ? 'attribute' : null"
            data-type="date-month"
          >
            <cat-label
              v-if="!hideFieldLabel"
              :label-id="
                (attributeFields && monthId ? monthId : id) + '-month-lbl'
              "
              :label="
                monthLabel ? monthLabel : resolveTxt('skin.month', 'Month')
              "
              :for-id="(attributeFields && monthId ? monthId : id) + '-month'"
              :optional="attributeFields ? !attrReq.month : !mandatory"
              :display-optional="
                mandatory || displaySettings.showOptionalOnGroupFields
              "
              class="placeholder"
            ></cat-label>
            <span
              class="dropdown-indicator"
              tabindex="-1"
              aria-hidden="true"
            ></span>
            <select
              :id="(attributeFields && monthId ? monthId : id) + '-month'"
              data-type="date-month"
              :readonly="readonly"
              :required="_errors"
              :aria-invalid="_attrErrors.length > 0"
              :aria-required="mandatory"
              :aria-label="
                hideFieldLabel
                  ? monthLabel
                    ? monthLabel
                    : resolveTxt('skin.month', 'Month')
                  : null
              "
              :class="_attrErrors.month.length > 0 ? 'invalidField' : ''"
              @change="monthChanged"
            >
              <option :selected="noMonthSelected" value>
                {{ resolveTxt("skin.month", "Month") }}
              </option>
              <option
                v-for="(month, idx) in months"
                :key="idx"
                :value="idx + 1"
                :selected="idx + 1 === monthValue"
              >
                {{ month }}
              </option>
            </select>
          </div>
        </template>
        <template v-else-if="field == 'y'">
          <cat-input
            v-if="!attributeFields || (attributeFields && yearId)"
            :id="(attributeFields && yearId ? yearId : id) + '-year'"
            class="cat-date-field-item cat-date-field-year"
            :label="yearLabel ? yearLabel : resolveTxt('skin.year', 'Year')"
            :data-label="
              yearLabel ? yearLabel : resolveTxt('skin.year', 'Year')
            "
            data-type="date-year"
            :label-handled="hideFieldLabel"
            :offscreen-label="
              hideFieldLabel
                ? yearLabel
                  ? yearLabel
                  : resolveTxt('skin.year', 'Year')
                : null
            "
            :placeholder="
              hideFieldLabel
                ? yearLabel
                  ? yearLabel
                  : resolveTxt('skin.year', 'Year')
                : null
            "
            :data-nodetype="attributeFields ? 'attribute' : null"
            :value="yearValue"
            :readonly="readonly"
            :mandatory="attributeFields ? attrReq.year : mandatory"
            mask="0123456789"
            :max-length="4"
            :errors="_attrErrors.year.length > 0"
            @ui-change="yearChanged"
            @ui-focus="_onFocus"
            @ui-blur="_onBlur"
            @ui-input="_onInput($event, 'year')"
          />
        </template>
      </template>
    </div>
    <div v-if="time" class="time">
      <cat-input
        :placeholder="resolveTxt('skin.hour', 'Hour')"
        :label="resolveTxt('skin.hour', 'Hour')"
        :value="hourValue"
        :readonly="readonly"
        :mandatory="mandatory"
        mask="0123456789"
        :max-length="2"
        regex="(0|1|2)?\d?"
        :errors="_errors && _errors.length > 0"
        @ui-change="hourChanged"
        @ui-input="_onInput($event, 'hour')"
      />
      <span role="presentation" class="datetime-presentation">:</span>
      <cat-input
        :placeholder="resolveTxt('skin.minute', 'Minute')"
        :label="resolveTxt('skin.minute', 'Minute')"
        :value="minuteValue"
        :readonly="readonly"
        :mandatory="mandatory"
        mask="0123456789"
        regex="(0|1|2|3|4|5)?\d?"
        :max-length="2"
        :errors="_errors && _errors.length > 0"
        @ui-change="minuteChanged"
        @ui-input="_onInput($event, 'minute')"
      />
      <span
        :aria-label="resolveTxt('skin.timezone', 'Timezone')"
        class="datetime-zone"
        >{{ zone }}</span
      >
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="_errors" :errors="_errors" />
    <div role="presentation" class="spacer"></div>
  </fieldset>
</template>

<script lang="ts">
import { defineComponent, inject, h } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatInput from "@/components/formelements/cat-input.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger";

export default defineComponent({
  setup(props) {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  provide: function () {
    return {
      showOptionals:
        this.mandatory || this.displaySettings.showOptionalOnGroupFields
    };
  },
  name: "CatAttributeDate",
  components: {
    CatErrors,
    CatInput,
    CatHelp,
    CatLabel,
    CatHelpTrigger,
    CatHint
  },
  props: {
    id: { type: [String, Number], description: "Date ID." },
    smallhint: {
      type: [String, Number],
      description: "Date field small hint text."
    },
    defaultValue: { type: [String, Date], description: "Default date value." },
    placeholder: { type: String, description: "Placeholder text." },
    label: { type: String, description: "Date field label." },
    dayLabel: { type: String, description: "Day field label." },
    monthLabel: { type: String, description: "Month field label." },
    yearLabel: { type: String, description: "Year field label." },
    dayId: { type: [String, Number], description: "Day field ID." },
    monthId: { type: [String, Number], description: "Month field ID." },
    yearId: { type: [String, Number], description: "Year field ID." },
    legendMandatory: {
      type: Boolean,
      default: false,
      description:
        "Indicates if the mandatory indicator should show in the legend. By default, the mandatory indicator is not displayed in the legend."
    },
    hideFieldLabel: {
      type: Boolean,
      default: false,
      description:
        "Hides the individual date fields labels. By default fields labels are visible."
    },
    value: { type: String, description: "Date field value." },
    errors: { type: Array, description: "Errors list." },
    attrErrors: { type: Object, description: "Attribute error." },
    attrReq: { type: Object, description: "Required attribute." },
    minDate: {
      type: [String, Date],
      description: "Earliest date that can be entered."
    },
    maxDate: {
      type: [String, Date],
      description: "Latest date that can be entered."
    },
    mandatory: {
      type: Boolean,
      description: "Flag to indicate if the field is mandatory."
    },
    readonly: {
      type: Boolean,
      description: "Shows date field in read-only mode."
    },
    hint: { type: String, description: "Date field hint text." },
    size: { type: String, description: "Date field size." },
    time: {
      type: Boolean,
      default: false,
      description: "Enables time field. By default disabled."
    },
    minYear: {
      type: [String, Number],
      default: () => new Date().getFullYear() - 100,
      description: "Earliest year that can be entered."
    },
    maxYear: {
      type: [String, Number],
      default: () => new Date().getFullYear(),
      description: "Latest year that can be entered."
    },
    help: { type: Object, description: "Help related information." },
    timeZone: { type: String, description: "Timezone aria description." },
    zone: {
      type: [String, Text],
      description: "Zone GMT.",
      default: "NZDT"
    },
    attributeFields: {
      type: Boolean,
      default: false,
      description: "Flag to indicate fields are individual attributes."
    },
    fieldOrder: {
      type: String,
      description:
        "Order of individual fields 'd','m' and 'y' separated by comma - . E.g. 'd,m,y'",
      validator: function (value) {
        let v = value.split(",").filter(String);
        return v.length > 0 && v.length < 4;
      },
      default: "d,m,y"
    }
  },
  mounted() {
    const date = this.parseDate(this.value);
    this.monthValue = date.month === 0 ? null : date.month;
    this.yearValue = date.year === 0 ? null : date.year;
    this.dayValue = date.day === 0 ? null : date.day;
  },
  data: function () {
    const date = this.parseDate(this.value);
    const resolveTxt = this.resolveTxt;
    var months = Array(12)
      .fill()
      .map(function (n, idx) {
        return resolveTxt("month." + (idx + 1), idx + 1);
      });
    return {
      monthValue: null,
      yearValue: null,
      dayValue: null,
      hourValue: date.hour,
      minuteValue: date.minute,
      months: months,
      userDay: null,
      userMonth: null,
      userYear: null,
      userHour: null,
      userMinute: null,
      hasFocus: null
    };
  },
  computed: {
    validateFieldOrder: function () {
      // Split the input string by commas, filtering out empty strings
      let newOrder = this.fieldOrder.split(",").filter(String);

      // Trim spaces from each string
      newOrder = newOrder.map((item) => item.trim());

      // Default/Allowed characters: d (day), m (month), y (year)
      const defaultOrder = ["d", "m", "y"];

      // Check for length and ensure no duplicates
      const hasValidLength = newOrder.length > 0 && newOrder.length < 4;

      // Check for duplicates by creating a Set
      const hasNoDuplicates = new Set(newOrder).size === newOrder.length;

      // Check if all characters are valid (i.e., in the allowed set)
      const allValidCharacters = newOrder.every((char) =>
        defaultOrder.includes(char)
      );

      if (hasValidLength && hasNoDuplicates && allValidCharacters) {
        return newOrder;
      } else {
        return defaultOrder;
      }
    },
    noMonthSelected: function () {
      return (
        this.monthValue === 0 ||
        this.monthValue === null ||
        this.monthValue === "" ||
        !this.monthValue
      );
    },
    _fs_class: function () {
      var numFields = 3;
      if (this.dayId || this.monthId || this.yearId) {
        // Control css only for partial dates.
        if (!this.dayId) {
          numFields--;
        }
        if (!this.monthId) {
          numFields--;
        }
        if (!this.yearId) {
          numFields--;
        }
      }
      var css = {
        fieldset: true,
        "cat-date-field": true,
        required: this.mandatory,
        optional: !this.mandatory,
        invalid:
          this._errors &&
          this._errors.length > 0 &&
          this._errors.some(function (r) {
            return r.level <= 1;
          }),
        "combo combo-2": numFields == 2,
        "combo combo-3": numFields == 3,
        date: !this.time,
        datetime: this.time,
        "readonly-list": this.readonly
      };
      css.warning =
        this._errors &&
        this._errors.length > 0 &&
        this._errors.some(function (r) {
          return r.level === 2 && !css.invalid;
        });
      css.infomessage =
        this._errors &&
        this._errors.length > 0 &&
        this._errors.some(function (r) {
          return r.level === 3 && !css.invalid;
        });
      css.debugmessage =
        this._errors &&
        this._errors.length > 0 &&
        this._errors.some(function (r) {
          return r.level === 4;
        });
      return css;
    },
    _ih_class: function () {
      return {
        small: this.size == "small",
        medium: this.size == "medium",
        large: this.size == "large" || !this.size,
        "input-holder": true,
        "cat-date-field-inner": true
      };
    },
    displaySettings: function () {
      return this.displayParams
        ? this.displayParams
        : { showOptionalOnGroupFields: false };
    },
    _attrErrors: function () {
      // Returns the attribute specific errors including the date container error
      var attrErrs = { day: [], month: [], year: [] };

      // Get the specific date attribute errors
      var errs = this.dateFieldErrors(this.attrErrors && this.attrErrors.day);
      attrErrs.day = errs.length > 0 ? errs : [];

      errs = this.dateFieldErrors(this.attrErrors && this.attrErrors.month);
      attrErrs.month = errs.length > 0 ? errs : [];

      errs = this.dateFieldErrors(this.attrErrors && this.attrErrors.year);
      attrErrs.year = errs.length > 0 ? errs : [];

      return attrErrs;
    },
    _errors: function () {
      // Returns all the errors from the date container errors and attribute specific errors
      var errors = [];

      // Get all the attribute errors first
      // For future consideration if the date fields order in the display becomes dynamic,
      // should the error message order matter too?
      var _t = this;
      var concatErrors = function (errs, field) {
        if (
          _t.attrErrors &&
          _t.attrErrors[field] &&
          _t.attrErrors[field].length > 0
        ) {
          return errs.concat(_t.attrErrors[field]);
        }
        return errs;
      };
      errors = concatErrors(errors, "day");
      errors = concatErrors(errors, "month");
      errors = concatErrors(errors, "year");

      // Get the generic date error message
      if (this.errors && this.errors.length > 0) {
        errors = errors.concat(this.errors);
      }
      return errors;
    }
  },
  watch: {
    value: function (newvalue, oldvalue) {
      if (newvalue != null && newvalue !== oldvalue) {
        const date = this.parseDate(newvalue);
        const day = this.userDay || (date.day === 0 ? null : date.day);
        const month = this.userMonth || (date.month === 0 ? null : date.month);
        const year = this.userYear || (date.year === 0 ? null : date.year);
        const hour = this.userHour || (date.hour === 0 ? null : date.hour);
        const minute =
          this.userMinute || (date.minute === 0 ? null : date.minute);

        this.dayValue = day;
        this.monthValue = month;
        this.yearValue = year;
        this.hourValue = hour;
        this.minuteValue = minute;
      } else if (newvalue === null || newvalue === "") {
        // clear out the values
        this.dayValue = null;
        this.monthValue = null;
        this.yearValue = null;
        this.hourValue = null;
        this.minuteValue = null;
      }
    }
  },
  methods: {
    dateFieldErrors: function (attrErrors) {
      // Doesn't matter if the error is attribute specific or whole date
      // Combine the attribute specific field error and whole date component error if any
      var errors = attrErrors && attrErrors.length > 0 ? attrErrors : [];
      if (this.errors && this.errors.length > 0) {
        errors = errors.concat(this.errors);
      }
      return errors;
    },

    _onFocus: function () {
      this.hasFocus = true;
    },

    _onBlur: function () {
      this.hasFocus = false;
    },

    _onInput: function (evt, input) {
      // Trigger emit change for Automated test
      // onInput only triggers if there is a difference between the local set value and .event.target.value
      const value = evt.event.target.value;

      if (input === "day" && this.userDay !== value) {
        this.dayValue = value;
        this.userDay = value;
        if (!this.hasFocus) {
          this.dateChanged(evt);
        }
      } else if (input === "year" && this.userYear !== value) {
        this.yearValue = value;
        this.userYear = value;
        if (!this.hasFocus || value.length == 4) {
          this.dateChanged(evt);
        }
      } else if (input === "hour" && this.userHour !== value) {
        this.hourValue = value;
        this.userHour = value;
        this.dateChanged(evt);
      } else if (input === "minute" && this.userMinute !== value) {
        this.minuteValue = value;
        this.userMinute = value;
        this.dateChanged(evt);
      }
    },

    yearChanged: function (evt) {
      this.yearValue = evt.value;
      this.userYear = evt.value;
      this.dateChanged(evt);
    },

    dayChanged: function (evt) {
      this.dayValue = evt.value;
      this.userDay = evt.value;
      this.dateChanged(evt);
    },

    monthChanged: function (evt) {
      this.monthValue = isNaN(parseInt(evt.target.value))
        ? null
        : parseInt(event.target.value);
      this.userMonth = this.monthValue;
      this.dateChanged(evt);
    },

    hourChanged: function (evt) {
      if (evt.value < 24) {
        this.hourValue = this.formatPad(evt.value, 2);
        this.userHour = this.formatPad(evt.value, 2);
        this.dateChanged(evt);
      } else {
        this.hourValue = "";
        this.userHour = "";
      }
    },

    minuteChanged: function (evt) {
      if (evt.value < 60) {
        this.minuteValue = this.formatPad(evt.value, 2);
        this.userMinute = this.formatPad(evt.value, 2);

        if (!this.hourValue && evt.value != "") {
          this.hourValue = this.formatPad(0, 2);
          this.userHour = this.formatPad(0, 2);
        }

        this.dateChanged(evt);
      } else {
        this.minuteValue = "";
        this.userMinute = "";
      }
    },

    parseDate: function (dtString) {
      let m,
        d,
        h,
        min,
        y = "";
      if (dtString) {
        if (dtString.indexOf("T") != -1) {
          const tmString = dtString.substring(
            dtString.indexOf("T") + 1,
            dtString.indexOf("T") + 6
          );
          dtString = dtString.substring(0, dtString.indexOf("T"));
          if (this.time) {
            const tm = tmString.split(":");
            h = isNaN(parseInt(tm[0])) ? "" : parseInt(tm[0]);
            min = isNaN(parseInt(tm[1])) ? "" : parseInt(tm[1]);
            if (!isNaN(parseInt(h)) && h < 10) {
              h = "0" + h;
            }
            if (!isNaN(parseInt(min)) && min < 10) {
              min = "0" + min;
            }
          }
        }
        if (dtString === "--") {
          m = null;
          d = null;
          y = null;
        }
        const pieces = dtString.split("-");
        y = isNaN(parseInt(pieces[0])) ? null : parseInt(pieces[0]);
        m = isNaN(parseInt(pieces[1])) ? null : parseInt(pieces[1]);
        d = isNaN(parseInt(pieces[2])) ? null : parseInt(pieces[2]);
      } else {
        m = null;
      }
      return {
        month: m,
        day: d,
        year: y,
        minute: min,
        hour: h
      };
    },
    valueString: function () {
      if (isNaN(parseInt(this.monthValue))) {
        return null;
      }
      const month = isNaN(parseInt(this.monthValue))
        ? null
        : ("00" + parseInt(this.monthValue)).slice(-2);
      const year = isNaN(parseInt(this.yearValue))
        ? null
        : ("0000" + this.yearValue).slice(-4);
      const day = isNaN(parseInt(this.dayValue))
        ? null
        : ("00" + this.dayValue).slice(-2);
      if (month !== null && year !== null && day !== null) {
        return year + "-" + month + "-" + day;
      }
      return null;
    },

    dateChanged: function (event, flush) {
      let valueString = this.valueString();
      if (valueString !== null && this.time) {
        if (this.hourValue && this.minuteValue) {
          valueString +=
            "T" + this.hourValue + ":" + this.minuteValue + ":00.000";
        } else {
          valueString = null;
        }
      }

      const emittedValue = {
        event: event,
        value: valueString,
        id: this.id ? this.id : null,

        // Date-related properties
        day: this.formatPad(this.dayValue, 2),
        month: this.formatPad(this.monthValue, 2),
        year: this.formatPad(this.yearValue, 4),

        // Time-related properties
        hour: this.hourValue,
        minute: this.minuteValue,

        // Use to refresh API call
        flush: flush
      };

      this.$emit("ui-change", emittedValue);
    },

    formatPad: function (num, size) {
      return num === null || num === ""
        ? null
        : String(num).padStart(size, "0");
    }
  }
});
</script>
